<template>
    <div class="min-h-screen flex bg-slate-50 overflow-hidden font-sans">
        <!-- Left side - Form -->
        <div
            class="w-full lg:w-1/2 flex items-center justify-center py-12 bg-white relative z-10"
            :style="{ paddingLeft: normalizeSize(90), paddingRight: normalizeSize(90) }"
        >
            <!-- Language Switcher -->
            <div class="absolute top-8 right-8 z-50">
                <LanguageSwitcher />
            </div>
            <div class="w-full h-full">
                <div class="mb-12">
                    <!-- Progress Indicator -->
                    <div class="flex items-center my-12 justify-center" :style="{ gap: normalizeSize(50) }">
                        <div class="h-1.5 rounded-full bg-[#7D2DBD] bg-opacity-30" :style="{ width: normalizeSize(160) }"></div>
                        <div class="h-1.5 rounded-full bg-[#7D2DBD] bg-opacity-30" :style="{ width: normalizeSize(160) }"></div>
                        <div class="h-1.5 rounded-full bg-slate-200" :style="{ width: normalizeSize(160) }"></div>
                    </div>

                    <h2 class="tracking-tight mb-3 text-center poppinsRegular" :style="{ fontSize: normalizeFontSize(22) }">
                        {{ $t('registration.tell_us_more') }}
                    </h2>
                </div>

                <div class="space-y-6">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6" :style="{ gap: normalizeSize(24, true) }">
                        <div class="space-y-2">
                            <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('registration.lastName') }}<span class="text-[#7D2DBD]">*</span>
                            </label>
                            <div
                                class="flex items-center bg-white rounded-xl border border-[#9c9af880] px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                                :style="{ height: normalizeSize(50, true) }"
                            >
                                <input
                                    type="text"
                                    v-model="localFormData.nom"
                                    :placeholder="$t('registration.placeholders.lastName')"
                                    class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                    :style="{ fontSize: normalizeFontSize(15) }"
                                />
                            </div>
                        </div>
                        <div class="space-y-2">
                            <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('registration.firstName') }}<span class="text-[#7D2DBD]">*</span>
                            </label>
                            <div
                                class="flex items-center bg-white rounded-xl border border-[#9c9af880] px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                                :style="{ height: normalizeSize(50, true) }"
                            >
                                <input
                                    type="text"
                                    v-model="localFormData.prenom"
                                    :placeholder="$t('registration.placeholders.firstName')"
                                    class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                    :style="{ fontSize: normalizeFontSize(15) }"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6" :style="{ gap: normalizeSize(24, true) }">
                        <div class="space-y-2">
                            <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('registration.email') }}<span class="text-[#7D2DBD]">*</span>
                            </label>
                            <div
                                class="flex items-center bg-white rounded-xl border px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                                :class="errors.email ? 'border-[#ed4b9e]' : 'border-[#9c9af880]'"
                                :style="{ height: normalizeSize(50, true) }"
                            >
                                <input
                                    type="email"
                                    v-model="localFormData.email"
                                    :placeholder="$t('registration.placeholders.email')"
                                    class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                    :style="{ fontSize: normalizeFontSize(15) }"
                                />
                                <span v-if="errors.email" class="ml-2 text-[#ed4b9e] font-bold text-sm">!</span>
                            </div>
                            <p v-if="errors.email" class="text-[12px] text-[#ed4b9e] mt-1 ml-1">{{ errors.email }}</p>
                        </div>

                        <div class="space-y-2">
                            <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('registration.phone') }}<span class="text-[#7D2DBD]">*</span>
                            </label>
                            <div class="flex items-end">
                                <div class="relative shrink-0 country-select-container">
                                    <vue-select
                                        v-model="localFormData.selectedCountryCode"
                                        :options="countries"
                                        :reduce="(c) => c.code"
                                        label="phone"
                                        :clearable="false"
                                        :searchable="false"
                                        class="country-select poppinsRegular"
                                        :style="{
                                            height: normalizeSize(50, true),
                                            width: normalizeSize(105, true),
                                            fontSize: normalizeFontSize(14),
                                        }"
                                        :append-to-body="true"
                                    >
                                        <template #option="option">
                                            <div class="flex items-center gap-2">
                                                <flag :iso="option.iso" v-if="option.iso" />
                                                <span v-else>🌍</span>
                                                <span>{{ option.phone }}</span>
                                            </div>
                                        </template>
                                        <template #selected-option="option">
                                            <div class="flex items-center gap-2">
                                                <flag :iso="option.iso" v-if="option.iso" />
                                                <span v-else>🌍</span>
                                                <span>{{ option.phone }}</span>
                                            </div>
                                        </template>
                                    </vue-select>
                                </div>
                                <div class="w-full">
                                    <div
                                        class="flex items-center bg-white border border-[#9c9af880] px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                                        :style="{
                                            height: normalizeSize(50, true),
                                            borderTopRightRadius: '12px',
                                            borderBottomRightRadius: '12px',
                                        }"
                                    >
                                        <input
                                            type="tel"
                                            v-model="localFormData.telephone"
                                            :placeholder="$t('registration.placeholders.phone')"
                                            class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                            :style="{ fontSize: normalizeFontSize(15) }"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="space-y-2">
                        <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('registration.company') }}<span class="text-[#7D2DBD]">*</span>
                        </label>
                        <div
                            class="flex items-center bg-white rounded-xl border border-[#9c9af880] px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                            :style="{ height: normalizeSize(50, true) }"
                        >
                            <input
                                type="text"
                                v-model="localFormData.entreprise"
                                :placeholder="$t('registration.placeholders.company')"
                                class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                :style="{ fontSize: normalizeFontSize(15) }"
                            />
                        </div>
                    </div>
                </div>

                <div class="mt-12 flex flex-col items-center gap-4">
                    <button
                        class="w-[80%] mx-auto bg-gradient-to-r from-[#40216F] to-[#182471] text-white rounded-[10px] border-[1.83px] border-white/50 hover:shadow-[inset_0_0_0_1000px_#00000066] transition-all duration-300 poppinsMedium disabled:opacity-65 disabled:pointer-events-none"
                        @click="continueToNextStep"
                        :disabled="!isFormValid || isCheckingEmail"
                        :style="{ height: normalizeSize(56, true), fontSize: normalizeFontSize(16) }"
                    >
                        <span
                            v-if="isCheckingEmail"
                            class="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2 inline-block align-middle"
                        ></span>
                        {{ $t('registration.start_now') }}
                    </button>
                    <button
                        class="text-slate-400 hover:text-slate-600 transition-colors poppinsMedium"
                        @click="goBack"
                        :style="{ fontSize: normalizeFontSize(14) }"
                    >
                        {{ $t('registration.back') }}
                    </button>
                </div>
            </div>
            <img
                src="/media/registerTenant/bgFormulaire.png"
                alt="bgFormulaire"
                class="z-[-1] absolute bottom-[20px] right-[-250px] object-contain w-[530px]"
            />
            <img
                src="/media/registerTenant/bgFormulaire.png"
                alt="bgFormulaire2"
                class="z-[-1] absolute bottom-[40%] left-[20px] object-contain w-[450px] opacity-60"
            />
        </div>

        <!-- Right side - Background -->
        <bg2Step
            :title="'Play like a Pro.'"
            :text="'Level up your workflow — discover our features to schedule, track, manage, report and connect. All the tools, in one place.'"
            :bottom-text="''"
            image="/media/registerTenant/bg3Step.png"
        />
    </div>
</template>

<script>
import axios from 'axios'
import bg2Step from './bg2Step.vue'
import normalizeSize from '../../../utils/normalizeSize'
import LanguageSwitcher from '../Parts/LanguageSwitcher.vue'
import 'vue-select/dist/vue-select.css'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'UserInfoStep',
    components: {
        bg2Step,
        LanguageSwitcher,
    },
    props: {
        formData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            localFormData: {
                nom: this.formData.nom || '',
                prenom: this.formData.prenom || '',
                email: this.formData.email || '',
                telephone: this.formData.telephone || '',
                phoneCountryCode: this.formData.phoneCountryCode || this.getDefaultPhonePrefix(),
                selectedCountryCode: '',
                entreprise: this.formData.entreprise || '',
            },
            errors: {},
            isCheckingEmail: false,
            countries: [
                { code: 'FR', name: 'France', phone: '+33', iso: 'fr' },
                { code: 'BE', name: 'Belgique', phone: '+32', iso: 'be' },
                { code: 'LU', name: 'Luxembourg', phone: '+352', iso: 'lu' },
                { code: 'CH', name: 'Suisse', phone: '+41', iso: 'ch' },
                { code: 'US', name: 'États-Unis', phone: '+1', iso: 'us' },
                { code: 'GB', name: 'Royaume-Uni', phone: '+44', iso: 'gb' },
                { code: 'ES', name: 'Espagne', phone: '+34', iso: 'es' },
                { code: 'IT', name: 'Italie', phone: '+39', iso: 'it' },
                { code: 'OTHER', name: 'Autre', phone: '+33', iso: '' },
            ],
        }
    },
    computed: {
        testimonials() {
            return [
                {
                    quote: this.$t('registration.reviews.quote1'),
                    name: this.$t('registration.reviews.author1'),
                    role: this.$t('registration.reviews.role1'),
                    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                },
                {
                    quote: this.$t('registration.reviews.quote2'),
                    name: this.$t('registration.reviews.author2'),
                    role: this.$t('registration.reviews.role2'),
                    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
                },
                {
                    quote: this.$t('registration.reviews.quote3'),
                    name: this.$t('registration.reviews.author3'),
                    role: this.$t('registration.reviews.role3'),
                    avatar: 'https://randomuser.me/api/portraits/women/26.jpg',
                },
                {
                    quote: this.$t('registration.reviews.quote4'),
                    name: this.$t('registration.reviews.author4'),
                    role: this.$t('registration.reviews.role4'),
                    avatar: 'https://randomuser.me/api/portraits/men/12.jpg',
                },
            ]
        },
        isFormValid() {
            return (
                this.localFormData.nom &&
                this.localFormData.prenom &&
                this.localFormData.email &&
                this.localFormData.entreprise &&
                this.validateEmail(this.localFormData.email)
            )
        },
    },
    watch: {
        'localFormData.selectedCountryCode': function (newVal) {
            const country = this.countries.find((c) => c.code === newVal)
            if (country) {
                this.localFormData.phoneCountryCode = country.phone
            }
        },
        'localFormData.email': function () {
            if (this.errors.email) {
                this.$set(this.errors, 'email', '')
            }
        },
    },
    created() {
        // Initialize selectedCountryCode based on phoneCountryCode
        const country = this.countries.find((c) => c.phone === this.localFormData.phoneCountryCode)
        if (country) {
            this.localFormData.selectedCountryCode = country.code
        } else {
            this.localFormData.selectedCountryCode = 'FR'
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        validateEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return re.test(email)
        },
        goBack() {
            this.$emit('prev-step')
        },
        async continueToNextStep() {
            if (!this.isFormValid || this.isCheckingEmail) return

            this.isCheckingEmail = true
            this.$set(this.errors, 'email', '') // Use $set to ensure reactivity

            try {
                const response = await axios.post('/register-tenant/check-email', {
                    email: this.localFormData.email,
                })

                if (response.data.available) {
                    this.$emit('update:formData', this.localFormData)
                    this.$emit('next-step')
                } else {
                    this.$set(this.errors, 'email', response.data.message)
                }
            } catch (error) {
                console.error('Error checking email availability:', error)
                if (error.response && error.response.data && error.response.data.message) {
                    this.$set(this.errors, 'email', error.response.data.message)
                } else {
                    this.$toast.error(this.$t('registration.email_verification_error'))
                }
            } finally {
                this.isCheckingEmail = false
            }
        },
        getDefaultPhonePrefix() {
            const browserLocale = navigator.language || navigator.userLanguage || 'fr'
            const parts = browserLocale.split('-')
            const lang = parts[0].toLowerCase()
            const region = parts[1] ? parts[1].toUpperCase() : null

            const countryMap = {
                FR: '+33',
                BE: '+32',
                LU: '+352',
                CH: '+41',
                US: '+1',
                GB: '+44',
                ES: '+34',
                IT: '+39',
            }

            if (region && countryMap[region]) {
                return countryMap[region]
            }

            const langMap = {
                fr: '+33',
                en: '+44',
                es: '+34',
                it: '+39',
                de: '+49',
            }

            return langMap[lang] || '+33'
        },
    },
}
</script>

<style scoped>
.font-sans {
    font-family: 'Poppins', sans-serif;
}

.text-primary {
    color: #2563eb;
}

.premium-input {
    width: 100%;
    height: 56px;
    padding: 0 20px;
    border-radius: 20px;
    border: 2px solid #f1f5f9;
    background: #f8fafc;
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    outline: none;
}

.premium-input::placeholder {
    color: #94a3b8;
    font-weight: 500;
}

.premium-input:focus {
    border-color: #2563eb;
    background: white;
    box-shadow:
        0 12px 24px -12px rgba(37, 99, 235, 0.3),
        0 0 0 4px rgba(37, 99, 235, 0.05);
    transform: translateY(-1px);
}

.premium-btn-primary {
    height: 64px;
    padding: 0 36px;
    background: #2563eb;
    color: white;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 15px 35px -12px rgba(37, 99, 235, 0.4);
    cursor: pointer;
}

.premium-btn-primary:hover:not(:disabled) {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 20px 40px -12px rgba(37, 99, 235, 0.5);
}

.premium-btn-primary:active:not(:disabled) {
    transform: scale(0.98);
}

.premium-btn-primary:disabled {
    opacity: 0.5;
    background: #94a3b8;
    box-shadow: none;
    cursor: not-allowed;
}

.premium-btn-secondary {
    height: 64px;
    background: white;
    color: #475569;
    border: 2px solid #f1f5f9;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 700;
    transition: all 0.2s ease;
    cursor: pointer;
}

.premium-btn-secondary:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #1e293b;
}

.glass-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.4;
    animation: move 20s infinite alternate cubic-bezier(0.45, 0, 0.55, 1);
}

.blob-1 {
    width: 500px;
    height: 500px;
    background: #2563eb;
    top: -150px;
    left: -150px;
}
.blob-2 {
    width: 600px;
    height: 600px;
    background: #3b82f6;
    bottom: -200px;
    right: -200px;
    animation-delay: -5s;
}
.blob-3 {
    width: 450px;
    height: 450px;
    background: #f97316;
    bottom: 5%;
    right: 5%;
    opacity: 0.25;
    animation-delay: -10s;
}

@keyframes move {
    from {
        transform: translate(0, 0) scale(1) rotate(0deg);
    }
    to {
        transform: translate(10%, 15%) scale(1.1) rotate(5deg);
    }
}

.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
}

.country-select-container :deep(.country-select) {
    --vs-border-color: #9c9af880;
    --vs-border-radius: 12px 0 0 12px;
    --vs-dropdown-bg: #fff;
    --vs-dropdown-color: #021832;
    --vs-dropdown-option-color: #021832;
    --vs-selected-color: #021832;
    --vs-search-input-color: #021832;
    --vs-controls-color: #021832;
}

.country-select-container :deep(.country-select .vs__dropdown-toggle) {
    height: 100%;
    border-right: none;
    padding: 0;
    display: flex;
    align-items: center;
}

.country-select-container :deep(.country-select .vs__selected-options) {
    display: flex;
    align-items: center;
    padding: 0;
    height: 100%;
}

.country-select-container :deep(.country-select .vs__selected) {
    margin: 0;
    padding: 0 0 0 12px;
    display: flex;
    align-items: center;
    position: relative;
    z-index: 10;
}

.country-select-container :deep(.country-select.vs--unsearchable .vs__search) {
    width: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    position: absolute !important;
    opacity: 0 !important;
}

.country-select-container :deep(.country-select .vs__actions) {
    padding: 0 8px 0 4px;
    height: 100%;
    display: flex;
    align-items: center;
}

.country-select-container :deep(.country-select .vs__open-indicator) {
    fill: #021832;
    transform: scale(0.65);
}
</style>

<style>
.vs__dropdown-menu {
    min-width: 200px !important;
    border: 1px solid #9c9af880 !important;
    border-radius: 12px !important;
    margin-top: 4px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    z-index: 9999 !important;
}
</style>
